import { fetchWithAuth } from '../utils/fetchWithAuth';
import { unwrapResponse as unwrap } from '../utils/unwrapResponse';
import type { MissionParticipant } from './interventionRequestsApi';

export interface IssueDetailSection {
  title: string;
  keys: string[];
}

// Only the config fields the admin detail page reads.
export interface IssueDetailServiceDesk {
  id: number;
  fieldsMapping?: Record<string, string>;
  detailsSections?: IssueDetailSection[];
  fieldsAllowedToDisplay?: Record<string, string[] | false>;
}

export interface IssueDetailFields {
  status: { id: string; name: string; statusCategory?: { key?: string } | null };
  priority?: { name: string } | null;
  created: string;
  updated: string;
}

export interface IssueDetail {
  id: string;
  key: string;
  // Named props we read + pass-through bag for custom fields (same approach
  // as IssueLike in utils/issueDetailFields.ts).
  fields: IssueDetailFields & Record<string, unknown>;
  mappedFields: {
    summary?: string;
    requestType?: { requestType?: { id: string; name: string } };
  } & Record<string, unknown>;
  names?: Record<string, string>;
  // Optional: a response served before the card existed carries none, and `undefined` is an empty
  // list, never an error (D52).
  participants?: MissionParticipant[];
  serviceDesk: IssueDetailServiceDesk | null;
}

export interface IssueAttachment {
  id: string;
  url: string;
  filename: string;
}

export interface IssueFormSummary {
  id: string;
  name: string;
  submitted: boolean;
}

// Sentinel errors: the page maps NOT_FOUND to the "cannot be found or no
// access" message and anything else to the generic retry message.
export const ISSUE_NOT_FOUND = 'NOT_FOUND';
export const ISSUE_LOAD_ERROR = 'LOAD_ERROR';

export const issueDetailApi = {
  fetchIssueDetail: async (issueKey: string): Promise<IssueDetail> => {
    const res = await fetchWithAuth(`/issues/${encodeURIComponent(issueKey)}`);
    if (!res.ok) {
      throw new Error([401, 403, 404].includes(res.status) ? ISSUE_NOT_FOUND : ISSUE_LOAD_ERROR);
    }
    const issue = await res.json();
    if (issue.error) throw new Error(ISSUE_LOAD_ERROR);
    return issue as IssueDetail;
  },

  fetchIssueAttachments: async (issueKey: string): Promise<IssueAttachment[]> => {
    const res = await fetchWithAuth(`/issues/${encodeURIComponent(issueKey)}/attachments-comments`);
    const data = await unwrap<{ attachments: IssueAttachment[] }>(res, 'Failed to fetch attachments');
    return data.attachments || [];
  },

  fetchIssueForms: async (issueId: string): Promise<IssueFormSummary[]> => {
    const res = await fetchWithAuth(`/issues/${encodeURIComponent(issueId)}/forms?light=true`);
    const data = await unwrap<IssueFormSummary[]>(res, 'Failed to fetch forms');
    return data || [];
  },
};
